import { create } from 'zustand';
import { sanitizeHex } from '@walletconnect/encoding';
import { BN } from 'bn.js';
import Web3 from 'web3';
import { useConnectionStore } from './connection';
import { TxStatus, OnFinishData } from './tx';
import { useWalletEnvStore } from './wallet-env';
import { useBalanceStore } from './balance';
import { isElectron } from '@shared/utils/is-electron';
import { LIFTING_CHAINS, MARKETPLACE_CHAINS } from '@shared/types/enums';
import isOnline from 'is-online';
import { AbiItem } from 'web3-utils';
import { useWalletStore } from './wallets';
import { useApiServiceStore } from './api';

// Helper ABIs
const TOKEN_ABI: AbiItem[] = [
  {
    constant: true,
    inputs: [],
    name: 'name',
    outputs: [{ name: '', type: 'string' }],
    type: 'function',
  },
  {
    constant: true,
    inputs: [{ name: 'owner', type: 'address' }],
    name: 'nonces',
    outputs: [{ name: '', type: 'uint256' }],
    type: 'function',
  },
  {
    constant: true,
    inputs: [
      { name: 'owner', type: 'address' },
      { name: 'spender', type: 'address' },
    ],
    name: 'allowance',
    outputs: [{ name: '', type: 'uint256' }],
    type: 'function',
  },
  {
    constant: false,
    inputs: [
      { name: 'spender', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    name: 'approve',
    outputs: [{ name: '', type: 'bool' }],
    type: 'function',
  },
];

export const MIN_LIFTING_AMOUNT = {
  [MARKETPLACE_CHAINS.EWC]: '10',
  [MARKETPLACE_CHAINS.ETH]: '0',
};

type LiftingStore = {
  status: TxStatus;
  lift: (
    receiverAddress: string,
    amount: number,
    network: LIFTING_CHAINS,
    onFinish: (onFinishData: OnFinishData) => void,
  ) => Promise<void>;
  reset: () => void;
};

export const useLiftingStore = create<LiftingStore>((set) => ({
  status: 'idle',
  lift: async (
    receiverAddress: string,
    amount: number,
    network: LIFTING_CHAINS,
    onFinish: (onFinishData: OnFinishData) => void,
  ) => {
    try {
      const { client, sessionEWC, sessionETH } = useConnectionStore.getState();
      const { walletConst } = useWalletEnvStore.getState();
      const { setBalanceEWC, setBalanceETH, fetchNetworkBalance } = useBalanceStore.getState();
      const { walletType } = useWalletStore.getState();

      const isEthNetwork = network === MARKETPLACE_CHAINS.ETH;
      const session = isEthNetwork ? sessionETH : sessionEWC;
      const setBalance = isEthNetwork ? setBalanceETH : setBalanceEWC;

      // Check if using Ledger wallet
      if (walletType === 'ledger' && isElectron()) {
        const api = useApiServiceStore.getState().api;
        const result = await api.deviceLifting(receiverAddress, amount, network);

        if (result.errorStatus) {
          set({ status: 'error' });
          onFinish({ status: 'error', errorMsg: result.errorStatus });
          return;
        }

        set({ status: 'success' });
        onFinish({ status: 'success', blockHash: result.txnHash });
        return;
      }

      // WalletConnect flow
      if (!session) {
        throw new Error(`No ${network} session`);
      }

      if (!client) {
        throw new Error('Client not initialized');
      }

      const minLiftingAmount = parseFloat(MIN_LIFTING_AMOUNT[network]);
      if (amount < Number(minLiftingAmount)) {
        set({ status: 'error' });
        throw new Error('Amount is less than the minimum');
      }

      set({ status: 'preparing' });

      // Get network-specific RPC endpoint and configuration
      const rpcEndpoint = isEthNetwork ? walletConst.ethRpc : walletConst.ewcRpc;
      const chainId = isEthNetwork ? walletConst.ethChainId : walletConst.ewcChainId;

      if (!rpcEndpoint) {
        throw new Error(`${network} RPC endpoint is not configured`);
      }

      if (!chainId) {
        throw new Error(`${network} chain ID is not configured`);
      }

      const httpProvider = new Web3.providers.HttpProvider(rpcEndpoint);
      const web3 = new Web3(httpProvider);

      // Use network-specific ABI and smart contract
      const contractAbi = isEthNetwork ? walletConst.ethAbi : walletConst.ewcAbi;
      const contractAddress = isEthNetwork
        ? walletConst.ethSmartContract
        : walletConst.ewcSmartContract;

      const ftsmContract = new web3.eth.Contract(contractAbi, contractAddress);
      const tokenAddress = walletConst.avtContractAddress;
      const value = Web3.utils.toWei(amount.toString(), 'ether');
      const senderAddress = String(session.namespaces.eip155.accounts[0].split(':')[2]);
      const t2PubKey = sanitizeHex(receiverAddress);

      if (!t2PubKey) {
        throw new Error(`T2 Public Key not configured for ${network}`);
      }

      // Helper function to check and approve token allowance
      const ensureTokenAllowance = async () => {
        const tokenContract = new web3.eth.Contract(TOKEN_ABI, tokenAddress);
        const allowance = await tokenContract.methods
          .allowance(senderAddress, contractAddress)
          .call();
        const allowanceBN = new BN(allowance);
        const valueBN = new BN(value);
        const gasPrice = await web3.eth.getGasPrice();

        if (allowanceBN.lt(valueBN)) {
          set({ status: 'preparing' });

          // Use appropriate gas configuration for approval transaction
          const approveGasConfig = isEthNetwork
            ? {
                gasPrice: gasPrice, // 200 Gwei for ETH network
              }
            : {
                gasPrice: 100000000000, // 100 Gwei for EWC network
                gas: 100000, // Fixed gas limit for EWC
              };

          const approveTx = {
            from: senderAddress,
            to: tokenAddress,
            data: tokenContract.methods.approve(contractAddress, value).encodeABI(),
            gasPrice: approveGasConfig.gasPrice,
            ...(approveGasConfig.gas && { gas: approveGasConfig.gas }),
            value: '0',
            nonce: Web3.utils.toHex(await web3.eth.getTransactionCount(senderAddress)),
          };

          await client.request({
            chainId: `eip155:${chainId}`,
            topic: session.topic,
            request: { method: 'eth_sendTransaction', params: [approveTx] },
          });

          set({ status: 'executing' });
          await new Promise((resolve) => setTimeout(resolve, 5000));
        }
      };

      // Helper function to try permit signature
      const tryPermitSignature = async () => {
        const tokenContract = new web3.eth.Contract(TOKEN_ABI, tokenAddress);

        const [tokenName, nonce] = await Promise.all([
          tokenContract.methods.name().call(),
          tokenContract.methods.nonces(senderAddress).call(),
        ]);

        const deadline = Math.floor(Date.now() / 1000) + 3600;
        const typedData = {
          domain: {
            name: tokenName,
            version: '1',
            chainId: parseInt(chainId),
            verifyingContract: tokenAddress,
          },
          types: {
            EIP712Domain: [
              { name: 'name', type: 'string' },
              { name: 'version', type: 'string' },
              { name: 'chainId', type: 'uint256' },
              { name: 'verifyingContract', type: 'address' },
            ],
            Permit: [
              { name: 'owner', type: 'address' },
              { name: 'spender', type: 'address' },
              { name: 'value', type: 'uint256' },
              { name: 'nonce', type: 'uint256' },
              { name: 'deadline', type: 'uint256' },
            ],
          },
          primaryType: 'Permit',
          message: {
            owner: senderAddress,
            spender: contractAddress,
            value: value,
            nonce: nonce,
            deadline: deadline,
          },
        };

        const availableMethods = session.namespaces.eip155?.methods || [];
        const hasSignTypedDataV4 = availableMethods.includes('eth_signTypedData_v4');
        const hasSignTypedData = availableMethods.includes('eth_signTypedData');

        set({ status: 'pending-confirmation' });

        // Try v4 first if available, then fallback to standard
        let signature: string;

        if (hasSignTypedDataV4) {
          try {
            console.log('🔐 Trying eth_signTypedData_v4 (method available in session)...');
            signature = (await client.request({
              chainId: `eip155:${chainId}`,
              topic: session.topic,
              request: {
                method: 'eth_signTypedData_v4',
                params: [senderAddress, JSON.stringify(typedData)],
              },
            })) as string;
            console.log('✅ eth_signTypedData_v4 succeeded!');
          } catch (error) {
            console.log(
              '❌ eth_signTypedData_v4 failed despite being available, trying fallback...',
              error,
            );
            if (!hasSignTypedData) {
              throw new Error(
                'Neither eth_signTypedData_v4 nor eth_signTypedData are available in this session',
              );
            }
            signature = (await client.request({
              chainId: `eip155:${chainId}`,
              topic: session.topic,
              request: { method: 'eth_signTypedData', params: [senderAddress, typedData] },
            })) as string;
            console.log('✅ eth_signTypedData fallback succeeded!');
          }
        } else if (hasSignTypedData) {
          console.log('🔐 eth_signTypedData_v4 not available, using eth_signTypedData...');
          signature = (await client.request({
            chainId: `eip155:${chainId}`,
            topic: session.topic,
            request: { method: 'eth_signTypedData', params: [senderAddress, typedData] },
          })) as string;
          console.log('✅ eth_signTypedData succeeded!');
        } else {
          throw new Error(
            'No compatible eth_signTypedData methods available in this session. Available methods: ' +
              availableMethods.join(', '),
          );
        }

        // Parse signature
        const r = signature.slice(0, 66);
        const s = '0x' + signature.slice(66, 130);
        const v = parseInt(signature.slice(130, 132), 16);

        return { deadline, v, r, s };
      };

      let transactionData: any;
      let gas: any;

      if (isEthNetwork) {
        // ETH: Try permitLift first, fallback to standard lift
        try {
          const { deadline, v, r, s } = await tryPermitSignature();
          gas = await ftsmContract.methods
            .permitLift(tokenAddress, t2PubKey, value, deadline, v, r, s)
            .estimateGas({ from: senderAddress });
          transactionData = ftsmContract.methods.permitLift(
            tokenAddress,
            t2PubKey,
            value,
            deadline,
            v,
            r,
            s,
          );
          console.log('✅ Using permitLift (no approval needed)');
        } catch (permitError) {
          console.log('🔄 PermitLift failed, falling back to standard lift', permitError);
          await ensureTokenAllowance();
          gas = await ftsmContract.methods
            .lift(tokenAddress, t2PubKey, value)
            .estimateGas({ from: senderAddress });
          transactionData = ftsmContract.methods.lift(tokenAddress, t2PubKey, value);
        }
      } else {
        // EWC: Use liftEWT (native EWT, no token approval needed)
        console.log('🔍 Using EWC liftEWT (native EWT, no approval needed)');
        transactionData = ftsmContract.methods.liftEWT(t2PubKey);
      }
      // Fetch appropriate balances for the network
      const balance = await fetchNetworkBalance(web3, isEthNetwork, senderAddress, tokenAddress);
      setBalance(balance);
      const gasPrice = await web3.eth.getGasPrice();

      // Set gas price and limit based on network
      // For EWC: Set gas to achieve exactly 0.01 EWT total fee
      // For ETH: Keep higher gas price to avoid spikes on mainnet/testnet
      const gasConfig = isEthNetwork
        ? {
            gasPrice: gasPrice, // 200 Gwei for ETH network
            // Let wallet/network decide gas limit for ETH
          }
        : {
            gasPrice: 100000000000, // 100 Gwei for EWC network
            gas: 100000, // Fixed gas limit for EWC (100k gas * 100 Gwei = 0.01 EWT)
            gasLimit: 4900000,
          };

      const gasLimitWithMargin = Math.floor(gas * 1.2);
      const tx = {
        from: senderAddress,
        to: contractAddress,
        data: transactionData.encodeABI(),
        value: isEthNetwork ? '0' : value, // ETH uses tokens, EWC sends native EWT
        nonce: Web3.utils.toHex(await web3.eth.getTransactionCount(senderAddress)),
        gas: isEthNetwork ? gas : gasConfig.gas,
        gasPrice: isEthNetwork ? gasPrice : gasConfig.gasPrice, // Only add gas limit for EWC
        gasLimit: gasLimitWithMargin,
      };

      set({ status: 'pending-confirmation' });

      const txnHash = await Promise.race([
        client.request({
          chainId: `eip155:${chainId}`,
          topic: session.topic,
          request: {
            method: 'eth_sendTransaction',
            params: [tx],
          },
        }) as Promise<string>,
        (async () => {
          let isInternet = await isOnline();
          /**
           * on Electron app it should just do a while loop until approved in wallet (no breaking change)
           * on Web app it's going to stop when there's no internet connection and this would resolve Promise.race with undefined
           */
          while (isInternet || isElectron()) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
            isInternet = await isOnline();
          }

          if (!isInternet && !isElectron) {
            return;
          }
        })(),
      ]);

      if (!txnHash) {
        set({ status: 'error' });
        onFinish({ status: 'error', errorMsg: 'Waiting for approval failed - no internet' });
        return;
      }

      set({ status: 'executing' });
      // TODO: Check if the transaction was successful
      await new Promise((resolve) => setTimeout(resolve, 1000));
      set({ status: 'success' });
      onFinish({ status: 'success', blockHash: txnHash });
    } catch (error) {
      let errorMsg: string;
      if (error instanceof Error) {
        errorMsg = error.message;
      } else if (typeof error === 'object' && error?.['message']) {
        errorMsg = error['message'];
      } else {
        errorMsg = 'An unknown error occurred';
      }

      set({ status: 'error' });
      onFinish({ status: 'error', errorMsg });
      console.error(error);
    }
  },
  reset: () => set({ status: 'idle' }),
}));
